import type { SupabaseClient } from "@supabase/supabase-js";

export interface AiUserContext {
  user_locale: string;
  user_country: string;
  user_currency: string;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface PriceAlertInput {
  description: string;
  category?: string;
  subcategory?: string;
  unit: string;
  currentPrice: number;
  historicalAvg?: number;
  historicalMin?: number;
  historicalMax?: number;
  historicalCount?: number;
}

export interface PriceAlertResult {
  severity: "critical" | "warning" | "info";
  message: string;
  recommendation: string;
  suggestedPrice: number | null;
  confidence: number;
  model?: string;
}

export interface PreSendReviewIssue {
  severity: "error" | "warning" | "info";
  title: string;
  detail: string;
}

export interface PreSendReviewResult {
  summary: string;
  issues: PreSendReviewIssue[];
}

export interface ProposalEmailResult {
  subject: string;
  body: string;
}

export interface MarginSuggestionResult {
  suggestedMargin: number;
  rationale: string;
  confidence: "high" | "medium" | "low";
  sampleSize: number;
}

export interface ExecutionDeviationAlert {
  taskId: string;
  severity: "warning" | "critical";
  title: string;
  message: string;
  completionRatio: number;
}

export interface InvoiceExtractionItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  suggestedCategory: string;
  suggestedSubcategory: string | null;
  suggestedMapping: string | null;
}

export interface InvoiceExtractionResult {
  supplier: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  currency: string | null;
  items: InvoiceExtractionItem[];
}

export interface ExecutiveReportResult {
  title: string;
  summary: string;
  highlights: string[];
  risks: string[];
  nextSteps: string[];
  body: string;
}

const readFunctionErrorBody = async (error: unknown): Promise<string | null> => {
  const response = (error as { context?: unknown })?.context;
  if (!response || typeof response !== "object" || !("text" in response)) return null;

  try {
    const text = await (response as Response).text();
    if (!text) return null;

    try {
      const parsed = JSON.parse(text) as { error?: unknown; message?: unknown };
      if (typeof parsed.error === "string") return parsed.error;
      if (typeof parsed.message === "string") return parsed.message;
    } catch {
      return text;
    }

    return text;
  } catch {
    return null;
  }
};

const resolveFunctionError = async (error: unknown): Promise<Error> => {
  if (error instanceof Error) {
    const functionBodyError = await readFunctionErrorBody(error);
    if (functionBodyError) return new Error(functionBodyError);

    if (error.message.includes("Failed to send a request to the Edge Function")) {
      return new Error(
        "A funcao de IA nao esta disponivel neste projeto Supabase. Confirma se as Edge Functions foram publicadas no mesmo projeto usado pelo frontend.",
      );
    }

    return error;
  }

  return new Error("Erro ao contactar o servico de IA.");
};

export async function generateItemDescription(
  supabase: SupabaseClient,
  params: {
    category?: string;
    subcategory?: string;
    familyName?: string;
    typeName?: string;
    unit?: string;
  } & AiUserContext,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-describe-item", {
    body: params,
  });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return data.description as string;
}

export async function sendChatMessage(
  supabase: SupabaseClient,
  messages: ChatMessage[],
  context: AiUserContext,
  pageContext?: string,
  budgetContext?: string,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-chat", {
    body: { messages, pageContext, budgetContext, ...context },
  });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return data.response as string;
}

export async function getPriceAlert(
  supabase: SupabaseClient,
  input: PriceAlertInput & AiUserContext,
): Promise<PriceAlertResult> {
  const { data, error } = await supabase.functions.invoke("ai-price-alert", {
    body: input,
  });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return data as PriceAlertResult;
}

export async function generateBudgetContent(
  supabase: SupabaseClient,
  input: AiUserContext & {
    mode: "scope" | "assumptions";
    projectType: string;
    items: string[];
    country?: string;
  },
): Promise<{ text: string }> {
  const { data, error } = await supabase.functions.invoke("ai-budget-content", {
    body: input,
  });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return { text: String(data.text ?? "") };
}

export async function runPreSendReview(
  supabase: SupabaseClient,
  input: AiUserContext & {
    budgetName: string;
    minimumMargin: number;
    marginPercentage: number;
    assumptions: string[];
    items: Array<{ description: string; quantity: number; unitPrice: number }>;
  },
): Promise<PreSendReviewResult> {
  const { data, error } = await supabase.functions.invoke("ai-budget-review", {
    body: input,
  });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return {
    summary: String(data.summary ?? ""),
    issues: Array.isArray(data.issues) ? (data.issues as PreSendReviewIssue[]) : [],
  };
}

export async function generateProposalEmail(
  supabase: SupabaseClient,
  input: AiUserContext & {
    budgetName: string;
    clientName: string;
    budgetType: "projects" | "works";
    totalPrice: number;
    currency: string;
    highlights: string[];
  },
): Promise<ProposalEmailResult> {
  const { data, error } = await supabase.functions.invoke("ai-proposal-email", { body: input });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return {
    subject: String(data.subject ?? ""),
    body: String(data.body ?? ""),
  };
}

export async function getMarginSuggestion(
  supabase: SupabaseClient,
  input: AiUserContext & {
    budgetType: "projects" | "works";
    projectType: string;
  },
): Promise<MarginSuggestionResult> {
  const { data, error } = await supabase.functions.invoke("ai-margin-suggestion", { body: input });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return {
    suggestedMargin: Number(data.suggestedMargin ?? 0),
    rationale: String(data.rationale ?? ""),
    confidence: (data.confidence as MarginSuggestionResult["confidence"]) ?? "low",
    sampleSize: Number(data.sampleSize ?? 0),
  };
}

export async function analyzeExecutionDeviations(
  supabase: SupabaseClient,
  input: AiUserContext & {
    budgetName: string;
    records: Array<{
      taskId: string;
      taskName: string;
      plannedQuantity: number;
      accumulatedQuantity: number;
      completionPct: number;
      unit: string;
    }>;
  },
): Promise<ExecutionDeviationAlert[]> {
  const { data, error } = await supabase.functions.invoke("ai-execution-analysis", { body: input });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return Array.isArray(data?.alerts) ? (data.alerts as ExecutionDeviationAlert[]) : [];
}

export async function extractInvoicePdf(
  supabase: SupabaseClient,
  input: AiUserContext & {
    fileName: string;
    fileBase64: string;
    candidateMappings: string[];
  },
): Promise<InvoiceExtractionResult> {
  const { data, error } = await supabase.functions.invoke("ai-invoice-extract", { body: input });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return {
    supplier: typeof data?.supplier === "string" ? data.supplier : null,
    invoiceNumber: typeof data?.invoiceNumber === "string" ? data.invoiceNumber : null,
    invoiceDate: typeof data?.invoiceDate === "string" ? data.invoiceDate : null,
    currency: typeof data?.currency === "string" ? data.currency : null,
    items: Array.isArray(data?.items) ? (data.items as InvoiceExtractionItem[]) : [],
  };
}

export async function generateExecutiveReport(
  supabase: SupabaseClient,
  input: AiUserContext & {
    budgetName: string;
    projectType: string;
    measurements: Array<Record<string, unknown>>;
    autos: Array<Record<string, unknown>>;
    deviations: ExecutionDeviationAlert[];
  },
): Promise<ExecutiveReportResult> {
  const { data, error } = await supabase.functions.invoke("ai-executive-report", { body: input });
  if (error) throw await resolveFunctionError(error);
  if (data?.error) throw new Error(data.error);
  return {
    title: String(data.title ?? ""),
    summary: String(data.summary ?? ""),
    highlights: Array.isArray(data.highlights) ? (data.highlights as string[]) : [],
    risks: Array.isArray(data.risks) ? (data.risks as string[]) : [],
    nextSteps: Array.isArray(data.nextSteps) ? (data.nextSteps as string[]) : [],
    body: String(data.body ?? ""),
  };
}
